import type { User } from '../dataProvider/types';
import {
  DISPOSITION_POLICY,
  type Disposition,
  type DispositionRule,
} from './erasureState';

/**
 * The erasure DISPOSITION-PLAN model (D-erasure, Phase 7 surface 6) - the inverse of the DSAR export.
 * Where the DSAR package ASSEMBLES the member's data to RETURN it, this assembles the same inventory
 * as a DISPOSITION PLAN: per record type, what erasure WOULD do (tombstone / anonymise-retain /
 * delete / untouched) and how many records the member has. It is the core SAFETY surface: the blast
 * radius is shown BEFORE the irreversible execute.
 *
 * READ-ONLY: it only ever reads to build the preview. The execute action (a separate, super_admin
 * gated handler) is the only thing that mutates.
 */

export interface DispositionLine extends DispositionRule {
  /**
   * How many records of this type the member has. `null` for a type that is not counted (the audit
   * trail, which is untouched), so the renderer shows the disposition without a misleading "0".
   */
  count: number | null;
}

export interface ErasureDispositionPlan {
  subjectUserId: number;
  memberName: string;
  memberEmail: string;
  lines: DispositionLine[];
}

export interface ErasureDispositionInput {
  user: User;
  /** Record counts per policy key (profile is the member themselves, so always 1). */
  counts: Record<string, number>;
}

/** The dispositions that actually act on data, for a one-line summary above the table. */
export const ACTING_DISPOSITIONS: ReadonlySet<Disposition> =
  new Set<Disposition>(['tombstone_anonymise', 'anonymise_retain', 'delete']);

export function buildErasureDispositionPlan(
  input: ErasureDispositionInput,
): ErasureDispositionPlan {
  const lines: DispositionLine[] = DISPOSITION_POLICY.map((rule) => ({
    ...rule,
    count:
      rule.key === 'profile'
        ? 1
        : rule.key === 'auditLog'
          ? null
          : (input.counts[rule.key] ?? 0),
  }));

  return {
    subjectUserId: input.user.id,
    memberName: input.user.name,
    memberEmail: input.user.email,
    lines,
  };
}
